
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Settings, Globe, Users, ExternalLink, Palette, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Organization } from "@/types/organization";
import { DeleteOrganizationDialog } from "./DeleteOrganizationDialog";
import { useState } from "react";

const OrganizationsList = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState<Organization | null>(null);

  const { data: organizations, isLoading, refetch } = useQuery({
    queryKey: ['organizations-admin'],
    queryFn: async (): Promise<Organization[]> => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const toggleStatus = async (org: Organization) => {
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ is_active: !org.is_active })
        .eq('id', org.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Organization ${org.is_active ? 'deactivated' : 'activated'} successfully`,
      });

      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update organization status",
        variant: "destructive",
      });
    }
  };

  const openSubdomain = (subdomain: string) => {
    // For development, we'll use localhost with org parameter
    // In production, this would be https://{subdomain}.yourapp.com
    const isDev = window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1');
    const url = isDev 
      ? `${window.location.origin}?org=${subdomain}`
      : `https://${subdomain}.yourapp.com`;
    
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteClick = (org: Organization) => {
    setOrganizationToDelete(org);
    setDeleteDialogOpen(true);
  };

  const handleDeleteComplete = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="border rounded-lg p-6" role="status" aria-label="Loading organizations">
        <div className="text-center text-muted-foreground">Loading organizations...</div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table role="table" aria-label="Organizations list">
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Organization</TableHead>
            <TableHead scope="col">Subdomain</TableHead>
            <TableHead scope="col">Theme</TableHead>
            <TableHead scope="col">Status</TableHead>
            <TableHead scope="col">Created</TableHead>
            <TableHead scope="col">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {organizations?.map((org) => (
            <TableRow key={org.id}>
              <TableCell>
                <div className="flex items-center space-x-3">
                  {org.logo_url ? (
                    <img 
                      src={org.logo_url} 
                      alt={`${org.name} logo`}
                      className="w-8 h-8 rounded object-contain"
                      style={{ maxWidth: '100%', maxHeight: '100%' }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center" aria-label="Default organization icon">
                      <Globe className="w-4 h-4" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium">{org.display_name || org.name}</div>
                    <div className="text-sm text-muted-foreground">{org.name}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <code className="bg-muted px-2 py-1 rounded text-sm">
                    {org.subdomain}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openSubdomain(org.subdomain)}
                    className="h-6 w-6 p-0"
                    aria-label={`Open ${org.subdomain} in new tab`}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Palette className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {org.theme_config ? 'Custom' : 'Default'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <Badge 
                  variant={org.is_active ? "default" : "secondary"}
                  className="cursor-pointer"
                  onClick={() => toggleStatus(org)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleStatus(org);
                    }
                  }}
                  aria-label={`Toggle organization status. Currently ${org.is_active ? 'active' : 'inactive'}`}
                >
                  {org.is_active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                <time dateTime={org.created_at}>
                  {new Date(org.created_at).toLocaleDateString()}
                </time>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/admin/organizations/${org.id}`)}
                    aria-label={`Manage ${org.name} organization`}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(org)}
                    disabled={org.subdomain === 'swift'}
                    aria-label={`Delete ${org.name} organization`}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <DeleteOrganizationDialog
        organization={organizationToDelete}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onDeleted={handleDeleteComplete}
      />
    </div>
  );
};

export default OrganizationsList;
